import {
  Box,
  Button,
  DismissIcon,
  Flex,
  Link,
  CustomBagIcon,
  Typography,
} from '@iheartjane/reefer';
import { useState } from 'react';

import { BloomBankLinkingModalWithPrompt } from '@jane/bloom/components/BloomBankLinkingModal/BloomBankLinkingModalWithPrompt';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useAuthModal } from '@jane/bloom/providers/authModalProvider';
import { getSignInPromptDefaults } from '@jane/bloom/resources/signInPromptTheme';

import styles from './mobileLoginCallout.module.css';

interface MobileLoginCalloutProps {
  /** A sign-in from this callout should chain into the bank-linking prompt. */
  canPromptBankLinking?: boolean;
  /** Persists the bank-linking prompt dismissal (14-day cookie). */
  dismissBankLinkingPrompt?: () => void;
  onDismiss: () => void;
}

export const MobileLoginCallout = ({
  canPromptBankLinking = false,
  dismissBankLinkingPrompt,
  onDismiss,
}: MobileLoginCalloutProps) => {
  const { pinnedStore, theme } = useRootLoaderData();
  const { openAuthModal } = useAuthModal();
  const [showBankLinking, setShowBankLinking] = useState(false);

  // After sign-in, prompt eligible non-Gold-members to link a bank
  const openAuthModalWithPrompt = () =>
    openAuthModal({
      onSuccess: canPromptBankLinking
        ? () => setShowBankLinking(true)
        : undefined,
    });
  const lp = theme.bloom?.login_prompt;
  const defaults = getSignInPromptDefaults(
    pinnedStore.crm_integration === 'alpineiq'
  );

  // Custom button colors are deliberately NOT read here — they are
  // desktop-only per the feature spec. Use || (not ??) so empty strings fall
  // through to defaults.
  const header = lp?.mobile_header || lp?.header || defaults.mobile_header;
  const body = lp?.mobile_copy || defaults.mobile_copy;
  const ctaLabel = lp?.cta_label || defaults.cta_label;

  return (
    <Box
      className={styles['container']}
      p={16}
      m={8}
      background="grays-white"
      borderRadius="lg"
      border="grays-light"
    >
      <Flex alignItems="center" gap={16} className={styles['promptIcon']}>
        <CustomBagIcon size="xl" color="inherit" />
        <Flex flexDirection="column">
          <Typography variant="body-semibold">{header}</Typography>
          <Typography>{body}</Typography>
          <Link onClick={() => openAuthModalWithPrompt()}>{ctaLabel}</Link>
        </Flex>

        <Button.Icon
          icon={<DismissIcon altText="dismiss login prompt" />}
          onClick={onDismiss}
          className={styles['dismissButton']}
        />
      </Flex>
      {showBankLinking && (
        <BloomBankLinkingModalWithPrompt
          onDismiss={() => dismissBankLinkingPrompt?.()}
          onRequestClose={() => setShowBankLinking(false)}
        />
      )}
    </Box>
  );
};
